/**
 * TenderCashflowModeler — Tab 6 inside the Tender Analytics module.
 *
 * Sections:
 *   1. Tender + version selector
 *   2. Contract terms editor (collapsible)
 *   3. Milestones grid (AG-Grid-lite — using table for now)
 *   4. CashflowChart
 *   5. KPI summary cards
 *   6. Sensitivity sliders
 *   7. Actions: Save / Clone / Export Excel
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import {
  Plus, Save, Copy, FileDown, Trash2, Calculator, Sparkles,
} from 'lucide-react';
import api from '@/lib/api';
import CashflowChart from './CashflowChart';

// ─── Defaults ──────────────────────────────────────────────────────────────
const DEFAULT_TERMS = {
  contractValue: 4500000000,
  currency: 'INR',
  contractDurationMonths: 36,
  estimatedStartDate: new Date().toISOString().slice(0, 10),
  advancePaymentPct: 10,
  advanceRecoveryStartMonth: 3,
  advanceRecoveryEndMonth: 18,
  retentionPct: 10,
  retentionReleaseMonths: 12,
  paymentTermsDays: 60,
  performanceGuaranteePct: 10,
  bgCostPctPerAnnum: 2,
  directCostPct: 72,
  indirectCostPct: 8,
  contingencyPct: 3,
  escalationPct: 0,
  ldRatePerDay: 500000,
  ldCapPct: 5,
  discountRate: 12,
};

const DEFAULT_MILESTONES = [
  { milestoneId: 'MS-001', milestoneName: 'Mobilization',     plannedMonth: 1,  invoiceValuePct: 5,  costValuePct: 3,  isKeyMilestone: true },
  { milestoneId: 'MS-002', milestoneName: 'Engineering 50%',  plannedMonth: 6,  invoiceValuePct: 10, costValuePct: 12, isKeyMilestone: false },
  { milestoneId: 'MS-003', milestoneName: 'Engineering 100%', plannedMonth: 12, invoiceValuePct: 10, costValuePct: 10, isKeyMilestone: true },
  { milestoneId: 'MS-004', milestoneName: 'Material Delivery',plannedMonth: 15, invoiceValuePct: 20, costValuePct: 25, isKeyMilestone: true },
  { milestoneId: 'MS-005', milestoneName: 'Erection 50%',     plannedMonth: 24, invoiceValuePct: 20, costValuePct: 20, isKeyMilestone: false },
  { milestoneId: 'MS-006', milestoneName: 'Erection 100%',    plannedMonth: 30, invoiceValuePct: 15, costValuePct: 15, isKeyMilestone: true },
  { milestoneId: 'MS-007', milestoneName: 'MC Certificate',   plannedMonth: 34, invoiceValuePct: 10, costValuePct: 10, isKeyMilestone: true },
  { milestoneId: 'MS-008', milestoneName: 'Final Handover',   plannedMonth: 36, invoiceValuePct: 10, costValuePct: 5,  isKeyMilestone: true },
];

const fmtINR = (v) => {
  if (v === null || v === undefined) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(1)} Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  return `₹${v.toFixed(0)}`;
};

const KpiCard = ({ label, value, hint, tone = 'default' }) => {
  const colors = {
    default: 'border-[#3A3F4A]',
    good: 'border-emerald-500/40',
    warn: 'border-amber-500/40',
    bad: 'border-red-500/40',
  };
  return (
    <Card className={`bg-[#1F2937] ${colors[tone] || colors.default} border`}>
      <CardContent className="p-3">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-lg font-bold text-white mt-1 truncate" title={value}>{value}</p>
        {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  );
};

const TenderCashflowModeler = ({ tenders = [] }) => {
  const [activeTenderId, setActiveTenderId] = useState(tenders[0]?.id || tenders[0]?.tender_id || '');
  const [cashflow, setCashflow] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [milestones, setMilestones] = useState(DEFAULT_MILESTONES);
  const [sensitivity, setSensitivity] = useState({
    costOverrunPct: 0, scheduleDelayMonths: 0, paymentDelayDays: 0, scopeChangePct: 0,
  });
  const [sensitivityResult, setSensitivityResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // ─── Load active cashflow for selected tender ────────────────────────────
  const loadCashflow = useCallback(async () => {
    if (!activeTenderId) return;
    setLoading(true);
    try {
      const r = await api.get(`/tenders/cashflows`, { params: { tenderId: activeTenderId } });
      const found = r.data?.cashflows?.[0];
      if (found) {
        setCashflow(found);
        setTerms({ ...DEFAULT_TERMS, ...(found.contractTerms || {}) });
        setMilestones(found.milestones || DEFAULT_MILESTONES);
        const chart = await api.get(`/tenders/cashflows/${found.id}/monthly`, { params: { format: 'chart' } });
        setChartData(chart.data?.chartData || []);
      } else {
        setCashflow(null);
        setTerms(DEFAULT_TERMS);
        setMilestones(DEFAULT_MILESTONES);
        setChartData([]);
      }
    } catch (err) {
      toast.error('Failed to load cashflow');
    } finally {
      setLoading(false);
    }
  }, [activeTenderId]);

  useEffect(() => { loadCashflow(); }, [loadCashflow]);

  // ─── Validation totals ──────────────────────────────────────────────────
  const totals = useMemo(() => ({
    inv: milestones.reduce((s, m) => s + Number(m.invoiceValuePct || 0), 0),
    cost: milestones.reduce((s, m) => s + Number(m.costValuePct || 0), 0),
  }), [milestones]);

  const invValid = Math.abs(totals.inv - 100) < 0.5;
  const costValid = Math.abs(totals.cost - 100) < 0.5;
  const allValid = invValid && costValid;

  // ─── Actions ────────────────────────────────────────────────────────────
  const handleCreateOrSave = async () => {
    if (!activeTenderId) return toast.error('Select a tender first');
    if (!allValid) return toast.error('Milestone Invoice% / Cost% must each total 100%');
    setLoading(true);
    try {
      if (cashflow?.id) {
        const r = await api.put(`/tenders/cashflows/${cashflow.id}`, { contractTerms: terms, milestones });
        setCashflow(r.data);
        toast.success(`Cashflow v${r.data.version} saved`);
      } else {
        const r = await api.post(`/tenders/cashflows`, {
          tenderId: activeTenderId,
          tenderNumber: tenders.find((t) => (t.id || t.tender_id) === activeTenderId)?.tender_reference || '',
          contractTerms: terms,
          milestones,
        });
        setCashflow(r.data);
        toast.success('Cashflow model created');
      }
      // Refresh chart
      const chart = await api.get(`/tenders/cashflows/${(cashflow?.id) || ''}/monthly`, { params: { format: 'chart' } }).catch(() => null);
      if (chart?.data) setChartData(chart.data.chartData);
      await loadCashflow();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async () => {
    if (!cashflow?.id) return;
    try {
      await api.post(`/tenders/cashflows/${cashflow.id}/clone`);
      toast.success('Cloned as new version');
      await loadCashflow();
    } catch { toast.error('Clone failed'); }
  };

  const handleExport = async () => {
    if (!cashflow?.id) return;
    try {
      const r = await api.get(`/tenders/cashflows/${cashflow.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `cashflow_${cashflow.tenderNumber || 'tender'}_v${cashflow.version}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const handleRunSensitivity = async () => {
    if (!cashflow?.id) return toast.error('Save the cashflow first');
    try {
      const r = await api.post(`/tenders/cashflows/${cashflow.id}/sensitivity`, sensitivity);
      setSensitivityResult(r.data);
      toast.success('Sensitivity computed');
    } catch { toast.error('Sensitivity failed'); }
  };

  const distributeEvenly = (field) => {
    if (!milestones.length) return;
    const each = 100 / milestones.length;
    setMilestones(milestones.map((m) => ({ ...m, [field]: parseFloat(each.toFixed(2)) })));
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  const summary = (sensitivityResult?.sensitivitySummary) || cashflow?.summary || {};

  return (
    <div className="space-y-4" data-testid="tender-cashflow-tab">
      {/* Tender selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-slate-300 text-sm whitespace-nowrap">Tender:</Label>
          <Select value={activeTenderId} onValueChange={setActiveTenderId}>
            <SelectTrigger className="w-80 bg-[#1F2937] border-[#3A3F4A] text-white" data-testid="cashflow-tender-select">
              <SelectValue placeholder="— pick a tender —" />
            </SelectTrigger>
            <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
              {tenders.map((t) => (
                <SelectItem key={t.id || t.tender_id} value={t.id || t.tender_id}
                             className="text-slate-200">
                  {t.tender_reference || t.tenderNumber || '—'} · {(t.title || t.tenderName || '').slice(0, 60)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {cashflow && <span className="text-xs text-slate-400">v{cashflow.version}</span>}
        <div className="flex-1" />
        {cashflow?.id && (
          <>
            <Button variant="outline" size="sm" onClick={handleClone} className="border-slate-600" data-testid="clone-cashflow-btn">
              <Copy className="w-3 h-3 mr-1" />Clone
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="border-slate-600" data-testid="export-cashflow-btn">
              <FileDown className="w-3 h-3 mr-1" />Export
            </Button>
          </>
        )}
        <Button onClick={handleCreateOrSave} disabled={loading || !allValid || !activeTenderId}
                className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]" data-testid="save-cashflow-btn">
          <Save className="w-3 h-3 mr-1" />{cashflow ? 'Save' : 'Create'}
        </Button>
      </div>

      {/* Contract Terms */}
      <Card className="bg-[#1F2937] border-[#3A3F4A]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[#6B5CE7]" />
            <h3 className="text-sm font-semibold text-white">Contract Terms</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {[
              ['contractValue', 'Contract Value (₹)', 'number'],
              ['contractDurationMonths', 'Duration (months)', 'number'],
              ['estimatedStartDate', 'Start Date', 'date'],
              ['currency', 'Currency', 'select:INR,USD,EUR,AED'],
              ['advancePaymentPct', 'Advance %', 'number'],
              ['advanceRecoveryStartMonth', 'Adv Recovery From', 'number'],
              ['advanceRecoveryEndMonth', 'Adv Recovery To', 'number'],
              ['retentionPct', 'Retention %', 'number'],
              ['retentionReleaseMonths', 'Retention Release (months)', 'number'],
              ['paymentTermsDays', 'Payment Terms (days)', 'number'],
              ['performanceGuaranteePct', 'BG %', 'number'],
              ['bgCostPctPerAnnum', 'BG Cost % p.a.', 'number'],
              ['directCostPct', 'Direct Cost %', 'number'],
              ['indirectCostPct', 'Indirect Cost %', 'number'],
              ['contingencyPct', 'Contingency %', 'number'],
              ['ldCapPct', 'LD Cap %', 'number'],
              ['ldRatePerDay', 'LD Rate /day', 'number'],
              ['discountRate', 'Discount Rate %', 'number'],
            ].map(([key, label, type]) => (
              <div key={key}>
                <Label className="text-slate-400 text-[10px] block mb-0.5">{label}</Label>
                {type.startsWith('select:') ? (
                  <Select value={String(terms[key] || '')} onValueChange={(v) => setTerms({ ...terms, [key]: v })}>
                    <SelectTrigger className="h-8 bg-[#111827] border-[#3A3F4A] text-white text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      {type.replace('select:', '').split(',').map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-slate-200">{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={type}
                    value={terms[key] ?? ''}
                    onChange={(e) => setTerms({ ...terms, [key]: type === 'number' ? Number(e.target.value) : e.target.value })}
                    className="h-8 bg-[#111827] border-[#3A3F4A] text-white text-xs"
                    data-testid={`term-${key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Milestones */}
      <Card className="bg-[#1F2937] border-[#3A3F4A]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Milestones</h3>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => distributeEvenly('invoiceValuePct')}
                      className="border-slate-600 text-xs h-7" data-testid="distribute-inv-btn">
                Even Invoice%
              </Button>
              <Button size="sm" variant="outline" onClick={() => distributeEvenly('costValuePct')}
                      className="border-slate-600 text-xs h-7" data-testid="distribute-cost-btn">
                Even Cost%
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMilestones([...milestones, {
                milestoneId: `MS-${String(milestones.length + 1).padStart(3, '0')}`,
                milestoneName: '', plannedMonth: 1, invoiceValuePct: 0, costValuePct: 0, isKeyMilestone: false,
              }])} className="border-emerald-600 text-emerald-400 text-xs h-7" data-testid="add-milestone-btn">
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="milestones-table">
              <thead>
                <tr className="text-slate-400 border-b border-[#3A3F4A]">
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Milestone</th>
                  <th className="text-right p-2">Month</th>
                  <th className="text-right p-2">Invoice %</th>
                  <th className="text-right p-2">Cost %</th>
                  <th className="text-center p-2">Key</th>
                  <th className="text-right p-2">Invoice (₹)</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((m, idx) => (
                  <tr key={idx} className="border-b border-[#3A3F4A]/50 hover:bg-slate-800/40">
                    <td className="p-2 text-slate-400">{idx + 1}</td>
                    <td className="p-2">
                      <Input value={m.milestoneName}
                             onChange={(e) => {
                               const next = [...milestones];
                               next[idx] = { ...m, milestoneName: e.target.value };
                               setMilestones(next);
                             }}
                             className="h-7 bg-[#111827] border-[#3A3F4A] text-white text-xs"
                             data-testid={`milestone-name-${idx}`} />
                    </td>
                    <td className="p-2">
                      <Input type="number" value={m.plannedMonth}
                             onChange={(e) => {
                               const next = [...milestones];
                               next[idx] = { ...m, plannedMonth: Number(e.target.value) };
                               setMilestones(next);
                             }}
                             className="h-7 w-16 ml-auto bg-[#111827] border-[#3A3F4A] text-white text-xs text-right"
                             data-testid={`milestone-month-${idx}`} />
                    </td>
                    <td className="p-2">
                      <Input type="number" step="0.01" value={m.invoiceValuePct}
                             onChange={(e) => {
                               const next = [...milestones];
                               next[idx] = { ...m, invoiceValuePct: Number(e.target.value) };
                               setMilestones(next);
                             }}
                             className="h-7 w-20 ml-auto bg-[#111827] border-[#3A3F4A] text-white text-xs text-right"
                             data-testid={`milestone-inv-${idx}`} />
                    </td>
                    <td className="p-2">
                      <Input type="number" step="0.01" value={m.costValuePct}
                             onChange={(e) => {
                               const next = [...milestones];
                               next[idx] = { ...m, costValuePct: Number(e.target.value) };
                               setMilestones(next);
                             }}
                             className="h-7 w-20 ml-auto bg-[#111827] border-[#3A3F4A] text-white text-xs text-right" />
                    </td>
                    <td className="p-2 text-center">
                      <Checkbox checked={!!m.isKeyMilestone}
                                onCheckedChange={(c) => {
                                  const next = [...milestones];
                                  next[idx] = { ...m, isKeyMilestone: !!c };
                                  setMilestones(next);
                                }} />
                    </td>
                    <td className="p-2 text-right text-slate-300">
                      {fmtINR((terms.contractValue || 0) * Number(m.invoiceValuePct || 0) / 100)}
                    </td>
                    <td className="p-2">
                      <Button size="sm" variant="ghost"
                              onClick={() => setMilestones(milestones.filter((_, i) => i !== idx))}
                              className="h-6 w-6 p-0 text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold border-t-2 border-[#3A3F4A]">
                  <td className="p-2 text-slate-400">Σ</td>
                  <td className="p-2 text-slate-400">TOTAL</td>
                  <td></td>
                  <td className={`p-2 text-right ${invValid ? 'text-emerald-400' : 'text-red-400'}`}>
                    {totals.inv.toFixed(2)}%
                  </td>
                  <td className={`p-2 text-right ${costValid ? 'text-emerald-400' : 'text-red-400'}`}>
                    {totals.cost.toFixed(2)}%
                  </td>
                  <td></td>
                  <td className="p-2 text-right text-slate-300">
                    {fmtINR((terms.contractValue || 0))}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          {!allValid && (
            <p className="text-xs text-red-400 mt-2">
              ⚠ Both Invoice% and Cost% totals must equal 100% before saving.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Cashflow chart */}
      {chartData.length > 0 && (
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-white mb-2">Monthly Cashflow</h3>
            <CashflowChart data={chartData} />
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      {Object.keys(summary).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Gross Profit" value={fmtINR(summary.grossProfit)} tone="good" />
          <KpiCard label="Gross Margin" value={`${(summary.grossMarginPct ?? 0).toFixed(1)}%`} tone="good" />
          <KpiCard label="Peak Funding" value={fmtINR(summary.peakFundingRequirement)}
                    hint={`Month ${summary.peakFundingMonth}`} tone="warn" />
          <KpiCard label="Break-Even" value={summary.breakEvenMonth ? `M${summary.breakEvenMonth}` : '—'} />
          <KpiCard label="Project IRR" value={summary.projectIRR != null ? `${summary.projectIRR}%` : 'N/A'} />
          <KpiCard label="NPV @12%" value={fmtINR(summary.projectNPV)} />
          <KpiCard label="Payback (mo)" value={summary.paybackPeriodMonths || '—'} />
          <KpiCard label="BG Cost" value={fmtINR(summary.totalBGCost)} />
          <KpiCard label="Max LD" value={fmtINR(summary.totalLDExposure)} tone="bad" />
          <KpiCard label="Total Invoicing" value={fmtINR(summary.totalInvoicing)} />
          <KpiCard label="Total Inflow" value={fmtINR(summary.totalCashInflow)} />
          <KpiCard label="Total Outflow" value={fmtINR(summary.totalCostOutflow)} />
        </div>
      )}

      {/* Sensitivity */}
      {cashflow?.id && (
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-white">Sensitivity / What-If</h3>
              {sensitivityResult && <span className="text-xs text-amber-400">(preview only — not saved)</span>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
              {[
                ['costOverrunPct', 'Cost Overrun', -10, 50, 1, '%'],
                ['scheduleDelayMonths', 'Schedule Delay', 0, 12, 1, ' mo'],
                ['paymentDelayDays', 'Payment Delay', 0, 120, 5, ' days'],
                ['scopeChangePct', 'Scope Change', -20, 30, 1, '%'],
              ].map(([key, label, min, max, step, suffix]) => (
                <div key={key}>
                  <div className="flex justify-between mb-1">
                    <Label className="text-slate-400 text-[10px]">{label}</Label>
                    <span className="text-slate-200 text-xs font-mono">
                      {sensitivity[key]}{suffix}
                    </span>
                  </div>
                  <Slider value={[sensitivity[key]]} min={min} max={max} step={step}
                          onValueChange={([v]) => setSensitivity({ ...sensitivity, [key]: v })}
                          data-testid={`sensitivity-${key}`} />
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleRunSensitivity} size="sm" variant="outline"
                      className="border-amber-500 text-amber-400" data-testid="run-sensitivity-btn">
                Apply Sensitivity — Preview Impact
              </Button>
              <Button onClick={() => { setSensitivity({ costOverrunPct: 0, scheduleDelayMonths: 0, paymentDelayDays: 0, scopeChangePct: 0 }); setSensitivityResult(null); }}
                      size="sm" variant="outline" className="border-slate-600">
                Reset to Base
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TenderCashflowModeler;
